import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../types/account';
import type { CurrentUser } from '../../types/user';
import type { InboxThreadFilters } from '../../types/inbox';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { DateRangePicker } from '../../components/ui/DateRangePicker';
import type { KindSegmentValue } from '../../lib/inboxView';

// Community Inbox — left filter rail. Emits a complete InboxThreadFilters on
// every change; the page owns the state + query. Search debounces 300ms.

const CHANNEL_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'metaads', label: 'Ad comments' },
  { value: 'googlebusiness', label: 'Google Business' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
];

const MIN_RATING_OPTIONS = [
  { value: '', label: 'Any rating' },
  { value: '5', label: '5 stars only' },
  { value: '4', label: '4+ stars' },
  { value: '3', label: '3+ stars' },
  { value: '2', label: '2+ stars' },
  { value: '1', label: '1+ stars' },
];

const HAS_REPLY_OPTIONS = [
  { value: '', label: 'All reviews' },
  { value: '0', label: 'Needs reply' },
  { value: '1', label: 'Replied' },
];

export function FilterRail({
  filters,
  onChange,
  accounts,
  users,
  kind,
}: {
  filters: InboxThreadFilters;
  onChange: (next: InboxThreadFilters) => void;
  accounts: Account[];
  users: CurrentUser[];
  kind: KindSegmentValue;
}) {
  const [searchDraft, setSearchDraft] = useState(filters.search ?? '');

  // 300ms debounce on free-text search.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchDraft.trim();
      if ((filters.search ?? '') !== trimmed) {
        onChange({ ...filters, search: trimmed || undefined });
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const brandOptions = useMemo(
    () =>
      accounts
        .filter((a) => a.accountType === 'brand')
        .map((a) => ({ value: a.id, label: a.name })),
    [accounts],
  );
  // Account options are scoped to communityEnabled accounts (brief §3) — the
  // dropdown only ever lists accounts actually in Community Management, matching
  // the account-worklist panel's roster.
  const accountOptions = useMemo(
    () =>
      accounts
        .filter((a) => a.communityEnabled ?? false)
        .map((a) => ({ value: a.id, label: a.name })),
    [accounts],
  );
  const amOptions = useMemo(
    () => users.map((u) => ({ value: u.uid, label: u.displayName || u.email })),
    [users],
  );
  const assigneeOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'Unassigned' },
      ...users.map((u) => ({ value: u.uid, label: u.displayName || u.email })),
    ],
    [users],
  );

  return (
    // shrink-0 for the same reason as the accounts panel below it: inside the
    // rail's scrolling column, flex children shrink by default and would squash
    // the filter controls instead of letting the rail scroll.
    <div className="flex w-full shrink-0 flex-col gap-3">
      <Input
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        placeholder={
          kind === 'review'
            ? 'Search reviewer or text…'
            : kind === 'dm'
              ? 'Search participant or message…'
              : 'Search author or comment…'
        }
        aria-label="Search threads"
      />
      <MultiSelectFilter
        label="Brand"
        options={brandOptions}
        selected={filters.brandAccountIds ?? []}
        onChange={(next) => onChange({ ...filters, brandAccountIds: next.length ? next : undefined })}
      />
      <MultiSelectFilter
        label="Account"
        options={accountOptions}
        selected={filters.accountIds ?? []}
        onChange={(next) => onChange({ ...filters, accountIds: next.length ? next : undefined })}
      />
      <MultiSelectFilter
        label="Account Manager"
        options={amOptions}
        selected={filters.accountManagerIds ?? []}
        onChange={(next) =>
          onChange({ ...filters, accountManagerIds: next.length ? next : undefined })
        }
      />
      <MultiSelectFilter
        label="Channel"
        options={CHANNEL_OPTIONS}
        selected={filters.channels ?? []}
        onChange={(next) =>
          onChange({
            ...filters,
            channels: next.length ? (next as InboxThreadFilters['channels']) : undefined,
          })
        }
      />
      <MultiSelectFilter
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.statuses ?? []}
        onChange={(next) =>
          onChange({
            ...filters,
            statuses: next.length ? (next as InboxThreadFilters['statuses']) : undefined,
          })
        }
      />
      <MultiSelectFilter
        label="Assignee"
        options={assigneeOptions}
        selected={filters.assigneeUids ?? []}
        onChange={(next) => onChange({ ...filters, assigneeUids: next.length ? next : undefined })}
      />
      <div>
        <Label className="text-xs text-slate-500">Date range</Label>
        <DateRangePicker
          from={filters.dateFrom}
          to={filters.dateTo}
          ariaLabel="Filter by date range"
          onChange={(dateFrom, dateTo) => onChange({ ...filters, dateFrom, dateTo })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Checkbox
          checked={filters.priorityOnly === true}
          onCheckedChange={(checked) =>
            onChange({ ...filters, priorityOnly: checked === true ? true : undefined })
          }
        />
        Priority only
      </label>

      {/* Review facets (Phase 2) — only in the Reviews tab. */}
      {kind === 'review' && (
        <>
          <div>
            <Label className="text-xs text-slate-500">Rating</Label>
            <SelectField
              size="sm"
              aria-label="Minimum rating"
              value={filters.minRating ? String(filters.minRating) : ''}
              options={MIN_RATING_OPTIONS}
              onValueChange={(v) =>
                onChange({ ...filters, minRating: v === '' ? undefined : Number(v) })
              }
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Reply status</Label>
            <SelectField
              size="sm"
              aria-label="Reply status"
              value={filters.hasReply === undefined ? '' : filters.hasReply ? '1' : '0'}
              options={HAS_REPLY_OPTIONS}
              onValueChange={(v) =>
                onChange({ ...filters, hasReply: v === '' ? undefined : v === '1' })
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
